import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Loader } from 'lucide-react';
import { Button, Card } from '@/components/ui';
import { Logo } from '@/components/layout';
import { useAuth } from '@/contexts/AuthContext';

export function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, refreshUser } = useAuth();
  const sessionId = searchParams.get('session_id');
  const isTrial = searchParams.get('trial') === 'true';
  const [isVerifying, setIsVerifying] = useState(true);

  useEffect(() => {
    // Refresh user data to get updated subscription status
    refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    // Wait for auth to finish loading before proceeding
    if (isLoading) return;

    // Brief delay to allow webhook processing
    const timer = setTimeout(() => {
      setIsVerifying(false);

      // If not authenticated, redirect to login with dashboard as destination
      if (!isAuthenticated) {
        navigate('/login', {
          replace: true,
          state: { from: { pathname: '/dashboard' } }
        });
        return;
      }

      // For all signups (trial or paid), redirect to dashboard
      // Users will configure their newsletter via the Daily Newsletter widget on first access
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 1500);
    }, 2000);
    return () => clearTimeout(timer);
  }, [sessionId, isTrial, navigate, isAuthenticated, isLoading]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Logo size="md" linkTo="/" />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="max-w-md w-full text-center p-8">
          {isVerifying ? (
            <>
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <Loader className="w-12 h-12 text-primary animate-spin" />
              </div>
              <h1 className="text-2xl font-bold mb-2">
                {isTrial ? 'Setting Up Your Trial...' : 'Processing Payment...'}
              </h1>
              <p className="text-text-secondary">
                {isTrial
                  ? 'Please wait while we set up your free trial.'
                  : 'Please wait while we confirm your subscription.'}
              </p>
            </>
          ) : isTrial ? (
            <>
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-success" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Welcome to Vettis!</h1>
              <p className="text-text-secondary mb-4">
                Your 7-day free trial has started. Explore all research modes from the dashboard.
              </p>
              <div className="flex items-center justify-center gap-2 text-primary">
                <Loader className="w-4 h-4 animate-spin" />
                <span className="text-sm">Redirecting to dashboard...</span>
              </div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-success" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Payment Successful!</h1>
              <p className="text-text-secondary mb-4">
                Thank you for subscribing to Vettis. Your account has been updated
                and you now have access to your plan features.
              </p>

              <div className="space-y-3">
                <Link to="/dashboard" className="block">
                  <Button variant="primary" className="w-full" rightIcon={<ArrowRight className="w-4 h-4" />}>
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
            </>
          )}
        </Card>
      </main>

      {/* Help Section */}
      <section className="border-t border-border py-8 px-4">
        <div className="max-w-md mx-auto text-center">
          <h2 className="font-semibold mb-3">Need Help?</h2>
          <p className="text-text-secondary text-sm mb-4">
            If you have questions about your subscription or need assistance,
            we're here to help.
          </p>
          <a href="mailto:contact@vettis.ai" className="text-primary hover:underline text-sm">
            Contact Support
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-6 px-4 text-center">
        <p className="text-text-muted text-sm">
          &copy; {new Date().getFullYear()} Vettis. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

